/**
 * DM-26 — Route `/docs/search?q=...` — выдача поиска по документации.
 * Минимальный MVP: текстовое поле + список результатов. Интеграция с
 * глобальным CommandPalette — отдельной задачей.
 */

import * as m from '@staiter/i18n';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { DocsLayout } from '~/components/docs';
import { searchDocs } from '~/lib/api/docs';
import { useAuthStore } from '~/store/auth';

const searchValidator = z.object({ q: z.string().optional() });

export const Route = createFileRoute('/_auth/docs/search')({
  component: DocsSearchPage,
  validateSearch: searchValidator,
});

const ROLE_FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Все роли' },
  { value: 'manager', label: 'Руководитель' },
  { value: 'accountant', label: 'Бухгалтер' },
  { value: 'reviewer', label: 'Старший бухгалтер' },
  { value: 'admin', label: 'Администратор' },
  { value: 'viewer', label: 'Аудитор' },
  { value: 'warehouse-operator', label: 'Кладовщик' },
  { value: 'shared', label: 'Общие' },
];

function DocsSearchPage(): JSX.Element {
  const search = useSearch({ from: '/_auth/docs/search' });
  const navigate = useNavigate();
  const userRole = useAuthStore((s) => s.user?.role ?? 'viewer');
  const initialQ = search.q ?? '';
  const [q, setQ] = useState(initialQ);
  const [roleFilter, setRoleFilter] = useState<string>('');

  const query = useQuery({
    queryKey: ['docs', 'search', q, roleFilter, userRole],
    queryFn: () => searchDocs(q, { locale: 'ru', limit: 30 }),
    enabled: q.trim().length >= 2,
  });

  const filteredResults =
    roleFilter && query.data
      ? query.data.results.filter((r) => r.role === roleFilter)
      : query.data?.results;

  return (
    <DocsLayout
      toc={undefined}
      currentRole={userRole}
      currentSlug={null}
      breadcrumbs={[{ label: 'Документация', href: '/docs' }, { label: 'Поиск' }]}
    >
      <div
        style={{
          padding: 'var(--space-7, 48px) var(--space-5, 24px) var(--space-5, 24px)',
          maxWidth: '72ch',
          margin: '0 auto',
        }}
      >
        <h1>{m.ui_poisk_po_dokumentacii()}</h1>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2, 8px)',
            margin: 'var(--space-4, 16px) 0',
          }}
        >
          <Search size={20} style={{ marginTop: 12 }} aria-hidden="true" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Например: мембранный элемент 8040"
            style={{
              flex: 1,
              padding: 'var(--space-3, 12px)',
              border: '1px solid var(--border, rgba(0,0,0,0.08))',
              borderRadius: 'var(--radius-md, 8px)',
              fontSize: 'var(--text-base, 16px)',
              background: '#ffffff',
              color: '#0a0a0a',
              colorScheme: 'light',
              appearance: 'none',
              WebkitAppearance: 'none',
            }}
          />
        </div>
        {/* Подсветка <mark> из snippet — semantic token --warn-bg-15 вместо browser yellow. */}
        <style>{`.docs-search-results mark { background: var(--warn-bg-15, oklch(0.95 0.12 95)); padding: 0 2px; border-radius: 2px; color: inherit; }`}</style>
        {q.trim().length < 2 ? (
          <div style={{ color: 'var(--text-muted)', padding: 'var(--space-4, 16px) 0' }}>
            {m.ui_vvedite_minimum_2_simvola_dlya_poiska()}
          </div>
        ) : query.isLoading ? (
          <div style={{ color: 'var(--text-muted)' }}>{m.ui_poisk()}</div>
        ) : query.data && query.data.results.length === 0 ? (
          <div
            style={{
              color: 'var(--text-muted)',
              padding: 'var(--space-5, 24px) 0',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 'var(--text-base, 16px)', marginBottom: 8 }}>
              Ничего не найдено по запросу «{q}».
            </div>
            <div style={{ fontSize: 'var(--text-sm, 14px)' }}>
              {m.ui_poprobuyte_sokratit_zapros_ili_proverit_napisanie()}
            </div>
          </div>
        ) : query.data ? (
          <div className="docs-search-results">
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 'var(--space-2, 8px)',
                alignItems: 'center',
                marginBottom: 'var(--space-3, 12px)',
              }}
            >
              {ROLE_FILTER_OPTIONS.map((opt) => {
                const count =
                  opt.value === ''
                    ? (query.data?.results.length ?? 0)
                    : (query.data?.results.filter((r) => r.role === opt.value).length ?? 0);
                if (opt.value !== '' && count === 0) return null;
                const active = roleFilter === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setRoleFilter(opt.value)}
                    style={{
                      padding: 'var(--space-1, 4px) var(--space-3, 12px)',
                      borderRadius: '999px',
                      border: '1px solid',
                      borderColor: active
                        ? 'var(--accent-strong, #3d4ed4)'
                        : 'var(--border, rgba(0,0,0,0.08))',
                      background: active
                        ? 'var(--accent-bg-15, rgba(91,108,255,0.15))'
                        : 'transparent',
                      color: active ? 'var(--accent-strong, #3d4ed4)' : 'var(--text-secondary)',
                      fontSize: 'var(--text-xs, 12px)',
                      fontWeight: active ? 600 : 500,
                      cursor: 'pointer',
                    }}
                  >
                    {opt.label} <span style={{ opacity: 0.7 }}>{count}</span>
                  </button>
                );
              })}
            </div>
            <div style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Найдено: {filteredResults?.length ?? 0}
              {roleFilter
                ? ` (фильтр: ${ROLE_FILTER_OPTIONS.find((o) => o.value === roleFilter)?.label})`
                : ''}
            </div>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {(filteredResults ?? []).map((r) => (
                <li
                  key={`${r.role}-${r.slug}`}
                  style={{
                    padding: 'var(--space-3, 12px) 0',
                    borderBottom: '1px solid var(--border, rgba(0,0,0,0.08))',
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      navigate({
                        to: '/docs/$role/$slug',
                        params: { role: r.role, slug: r.slug },
                        search: { q },
                      })
                    }
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      textAlign: 'left',
                      width: '100%',
                    }}
                  >
                    <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{r.title}</div>
                    <div
                      style={{
                        fontSize: 'var(--text-xs, 12px)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {r.role} · {r.slug}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--text-sm, 14px)',
                        color: 'var(--text-secondary)',
                        marginTop: 4,
                      }}
                      // biome-ignore lint/security/noDangerouslySetInnerHtml: snippet_html приходит из PG ts_headline — только <mark> вокруг совпадений, остальное экранировано на стороне БД.
                      dangerouslySetInnerHTML={{ __html: r.snippet_html }}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </DocsLayout>
  );
}
